/**
 * Protocolo de tiempo real entre navegador y servidor.
 *
 * Regla que gobierna todo este archivo: el cliente nunca recibe la respuesta
 * correcta antes de que el servidor cierre la pregunta. Por eso existen los
 * tipos `Public*` — son las preguntas con la solución quirúrgicamente extraída.
 */

export type QuestionType =
  | 'multiple_choice'
  | 'true_false'
  | 'multi_select'
  | 'fill_blank'
  | 'match_pairs';

export type GameMode = 'ffa' | 'teams' | 'async_study';
export type RoomStatus = 'lobby' | 'countdown' | 'question' | 'reveal' | 'podium' | 'closed';

/* ------------------------------------------------------------------ *
 * Preguntas: forma privada (servidor) y forma pública (cliente)
 * ------------------------------------------------------------------ */

export interface OptionAuthor {
  id: string;
  text: string;
  isCorrect: boolean;
}

export interface PairAuthor {
  id: string;
  left: string;
  right: string;
}

/** Lo que guarda el servidor. Nunca sale tal cual por el socket. */
export interface QuestionAuthor {
  id: string;
  type: QuestionType;
  title: string;
  mediaUrl?: string;
  timeLimit: number;
  pointsMultiplier: 0 | 1 | 2;
  options?: OptionAuthor[];
  acceptedAnswers?: string[];
  pairs?: PairAuthor[];
  explanation?: string;
}

/** Lo que sí viaja al jugador mientras la pregunta está abierta. */
export interface PublicQuestion {
  id: string;
  index: number;
  total: number;
  type: QuestionType;
  title: string;
  mediaUrl?: string;
  timeLimit: number;
  pointsMultiplier: 0 | 1 | 2;
  /** Sin `isCorrect`. */
  options?: { id: string; text: string }[];
  /** Para emparejar: columnas barajadas por separado, sin la correspondencia. */
  leftItems?: { id: string; text: string }[];
  rightItems?: { id: string; text: string }[];
}

/* ------------------------------------------------------------------ *
 * Respuestas del jugador
 * ------------------------------------------------------------------ */

export type AnswerPayload =
  | { kind: 'choice'; optionId: string }
  | { kind: 'multi'; optionIds: string[] }
  | { kind: 'text'; text: string }
  | { kind: 'pairs'; links: { leftId: string; rightId: string }[] };

/* ------------------------------------------------------------------ *
 * Estado de sala
 * ------------------------------------------------------------------ */

export interface PublicPlayer {
  id: string;
  displayName: string;
  avatar: unknown;
  isHost: boolean;
  connected: boolean;
  team?: string;
  score: number;
  streak: number;
  /** Visible en la espera: sabes quién ya contestó, no qué contestó. */
  hasAnswered: boolean;
  /** Salas que dirige el servidor: el jugador marcó que está listo para empezar. */
  ready: boolean;
}

export interface RoomSettings {
  gameMode: GameMode;
  /** Bote de Dahus que el anfitrión pone para el podio. Puntos internos, no cripto. */
  prizePool: number;
  randomizeQuestions: boolean;
  randomizeOptions: boolean;
  showLeaderboardBetweenRounds: boolean;
  allowLateJoin: boolean;
  /** Cuántos jugadores pueden entrar, sin contar al anfitrión. */
  maxPlayers: number;
  /** Si la sala aparece en la sala de espera pública de la portada. */
  isPublic: boolean;
  /** Si el anfitrión también contesta y compite por el podio. */
  hostPlays: boolean;
  /**
   * Supervisión anti-trampas: la pantalla de cada jugador se graba (clics y
   * cambios en la interfaz de Open Dahu, nada fuera de la pestaña), el
   * anfitrión puede verla en vivo y queda guardada para revisarla después.
   * Los jugadores lo ven anunciado antes de entrar y durante toda la partida.
   */
  monitorPlayers: boolean;
}

/** Avisos de posible trampa de un jugador, solo para el anfitrión. */
export interface MonitorAlert {
  /** Veces que dejó la pestaña (cambió de pestaña o minimizó). */
  hidden: number;
  /** Veces que la ventana perdió el foco sin ocultarse (otra ventana encima). */
  blur: number;
  paste: number;
  copy: number;
  lastAt: number;
}

export type MonitorFlagKind = 'hidden' | 'blur' | 'paste' | 'copy';

/**
 * Tope de jugadores por sala, sin contar al anfitrión: 49 + 1 = 50 personas
 * conectadas, que es el aforo total del servidor.
 */
export const MAX_PLAYERS_LIMIT = 49;
export const MIN_PLAYERS_LIMIT = 2;

/** Tope de preguntas por trivia. Lo comparten el editor y la validación del servidor. */
export const MAX_QUESTIONS_PER_QUIZ = 50;

/**
 * Quién puede ver una trivia, como los vídeos de YouTube:
 * - `public`: sale en el catálogo y en la búsqueda.
 * - `unlisted`: no sale en ningún listado, pero quien tenga el enlace la juega.
 * - `private`: solo su autor; el enlace no sirve a nadie más.
 */
export type QuizVisibility = 'public' | 'unlisted' | 'private';

export const QUIZ_VISIBILITIES: readonly QuizVisibility[] = ['public', 'unlisted', 'private'];

export function normalizeVisibility(value: unknown): QuizVisibility | null {
  return QUIZ_VISIBILITIES.includes(value as QuizVisibility) ? (value as QuizVisibility) : null;
}

/**
 * Una sala abierta tal como se anuncia en la sala de espera. Solo lo necesario
 * para decidir si entrar: nada de la lista de jugadores ni de sus nombres.
 */
export interface LobbyRoom {
  pin: string;
  /**
   * `waiting`: abierta y sin empezar. `preparing`: la IA está escribiendo la
   * trivia. `playing`: la partida ya va por alguna pregunta; sigue anunciada
   * para que se vea que hay juego, y admite entrar si la sala lo permite.
   */
  status: 'waiting' | 'preparing' | 'playing';
  /** En partidas en curso: por qué pregunta van (desde 1). */
  questionNumber: number;
  /** Entrar ahora es posible (con sitio y, si ya empezó, admite entrar tarde). */
  joinable: boolean;
  /** La dirige el propio servidor: no hay anfitrión humano. */
  serverHosted: boolean;
  /** Hora del servidor a la que empieza sola, si se fijó. */
  startAt: number | null;
  quizTitle: string;
  coverEmoji: string;
  questionCount: number;
  hostName: string;
  gameMode: GameMode;
  prizePool: number;
  players: number;
  maxPlayers: number;
  createdAt: number;
  /** La sala graba la pantalla de los jugadores; se anuncia antes de entrar. */
  monitored: boolean;
  /** El anfitrión compite (y puede ganar el bote); se anuncia antes de entrar. */
  hostPlays: boolean;
}

export interface PublicRoom {
  pin: string;
  quizTitle: string;
  hostId: string;
  /**
   * La partida la dirige el servidor: nadie tiene controles de anfitrión, la
   * partida empieza cuando la gente está lista y avanza sola.
   */
  serverHosted: boolean;
  /** Quién creó la sala. En salas del servidor es quien puede cambiar la hora de inicio. */
  creatorId: string;
  creatorName: string;
  /** Mientras la IA escribe la trivia, qué se le dice a la sala. */
  preparing: string | null;
  /** Hora del servidor a la que la sala hará algo sola (empezar, pasar de pregunta). */
  autoAt: number | null;
  /** Hora del servidor fijada por el anfitrión para empezar sola. */
  startAt: number | null;
  status: RoomStatus;
  settings: RoomSettings;
  questionIndex: number;
  totalQuestions: number;
  players: PublicPlayer[];
}

export interface RevealPayload {
  questionIndex: number;
  explanation?: string;
  /** Solución, ahora sí. */
  correctOptionIds?: string[];
  correctPairs?: { leftId: string; rightId: string }[];
  acceptedAnswers?: string[];
  /** Cuántos eligieron cada opción — para el gráfico del anfitrión. */
  distribution: Record<string, number>;
  correctCount: number;
  answeredCount: number;
  totalPlayers: number;
  /** Resultado personal de quien recibe el mensaje. */
  you?: {
    isCorrect: boolean;
    pointsAwarded: number;
    responseTimeMs: number;
    streak: number;
    newScore: number;
  };
  standings: { id: string; displayName: string; avatar: unknown; score: number; delta: number; streak: number }[];
  /** En partidas por equipos: clasificación por equipo (y `standings` va vacío). */
  teamStandings?: TeamStanding[];
}

export interface TeamStanding {
  /** Id del equipo; "" agrupa a quienes no eligieron equipo. */
  team: string;
  score: number;
  delta: number;
  members: number;
}

export interface PodiumEntry {
  id: string;
  team?: string;
  displayName: string;
  avatar: unknown;
  score: number;
  accuracy: number;
  bestStreak: number;
  ratingChange: number;
  newRating: number;
  /** Dahus repartidos del bote. */
  dahusAwarded: number;
}

/* ------------------------------------------------------------------ *
 * Informe descargable de la partida
 * ------------------------------------------------------------------ */

export interface ReportQuestion {
  index: number;
  title: string;
  type: QuestionType;
  timeLimit: number;
  pointsMultiplier: 0 | 1 | 2;
  /** La solución, ya en texto legible. */
  correctAnswer: string;
  explanation?: string;
  /** Totales de toda la sala: no identifican a nadie. */
  answeredCount: number;
  correctCount: number;
  avgResponseMs: number;
  totalPlayers: number;
}

export interface ReportAnswer {
  questionIndex: number;
  answered: boolean;
  /** Lo que respondió, en texto legible. Vacío si no respondió. */
  answer: string;
  isCorrect: boolean;
  /** 0..1 — crédito parcial en selección múltiple y emparejar. */
  correctness: number;
  points: number;
  responseTimeMs: number;
}

export interface ReportPlayer {
  id: string;
  displayName: string;
  isGuest: boolean;
  placement: number;
  score: number;
  accuracy: number;
  correctCount: number;
  answeredCount: number;
  bestStreak: number;
  avgResponseMs: number;
  answers: ReportAnswer[];
}

export interface GameReport {
  /** `full`: todos los jugadores (anfitrión). `self`: solo quien lo recibe. */
  scope: 'full' | 'self';
  pin: string;
  quizTitle: string;
  gameMode: GameMode;
  finishedAt: number;
  totalPlayers: number;
  questions: ReportQuestion[];
  players: ReportPlayer[];
}

/* ------------------------------------------------------------------ *
 * Salas que dirige el servidor
 * ------------------------------------------------------------------ */

/** De dónde sale la trivia de una partida creada desde la sala de espera. */
export type AutoQuizSource =
  /** Una concreta. */
  | { kind: 'pick'; quizId: string }
  /** Cualquiera del catálogo público. */
  | { kind: 'random' }
  /** Una al azar de entre las que se marcaron. */
  | { kind: 'randomFrom'; quizIds: string[] }
  /** La escribe la IA. Sin `topic`, el tema lo elige ella. */
  | { kind: 'ai'; topic: string | null; count: number; language?: string };

/** Cuántas trivias se pueden marcar en `randomFrom`. */
export const MAX_RANDOM_POOL = 30;

/** Ajustes que se pueden elegir al crear una sala dirigida por el servidor. */
export type AutoRoomSettings = Pick<
  RoomSettings,
  'gameMode' | 'maxPlayers' | 'isPublic' | 'allowLateJoin' | 'randomizeQuestions'
>;

/* ------------------------------------------------------------------ *
 * Amigos
 * ------------------------------------------------------------------ */

export interface FriendSummary {
  id: string;
  username: string;
  avatar: unknown;
  dRating: number;
  /** Tiene la aplicación abierta ahora mismo. */
  online: boolean;
  /** PIN de la sala en la que está, si está en una. */
  inRoom: boolean;
}

export interface FriendRequestSummary {
  id: string;
  username: string;
  avatar: unknown;
  createdAt: string;
}

/** Invitación a una sala que un amigo envía en vivo. */
export interface RoomInvite {
  pin: string;
  fromId: string;
  fromName: string;
  quizTitle: string;
  coverEmoji: string;
}

/* ------------------------------------------------------------------ *
 * Mensajes
 * ------------------------------------------------------------------ */

export type ClientMessage =
  // `guestAvatar` solo lo mira el servidor cuando no hay sesión: quien tiene
  // cuenta usa el avatar guardado, no el que anuncie su navegador.
  | {
      t: 'auth';
      token: string | null;
      guestName?: string;
      guestAvatar?: object;
      /**
       * Identidad de invitado de una conexión anterior, para recuperar el sitio
       * tras una caída. Sin esto, cada reconexión creaba un jugador nuevo y la
       * sala acumulaba fantasmas de la misma persona.
       */
      guestId?: string;
      guestToken?: string;
    }
  /** Cambia el apodo y el avatar sin rehacer la conexión. */
  | { t: 'guest:identify'; guestName?: string; guestAvatar?: object }
  | {
      t: 'room:create';
      quizId: string;
      settings: Partial<RoomSettings>;
      startAt?: number | null;
      inviteIds?: string[];
    }
  /** Abre una sala que dirige el servidor, desde la sala de espera. */
  | {
      t: 'room:createAuto';
      source: AutoQuizSource;
      settings: Partial<AutoRoomSettings>;
      startAt?: number | null;
      /** Amigos a los que invitar en cuanto la sala exista. */
      inviteIds?: string[];
    }
  /** En salas del servidor: el jugador está (o deja de estar) listo para empezar. */
  | { t: 'room:ready'; ready: boolean }
  /** Invita a amigos a la sala en la que estás. */
  | { t: 'room:invite'; friendIds: string[] }
  | { t: 'room:join'; pin: string }
  | { t: 'room:leave' }
  /** Empieza (o deja) de recibir la lista de salas públicas en vivo. */
  | { t: 'lobby:subscribe' }
  | { t: 'lobby:unsubscribe' }
  | { t: 'room:setTeam'; team: string }
  | { t: 'host:start' }
  | { t: 'host:next' }
  | { t: 'host:skip' }
  | { t: 'host:kick'; playerId: string }
  | { t: 'host:settings'; settings: Partial<RoomSettings> }
  /**
   * Fija la hora (del servidor) a la que la partida empieza sola, o la quita
   * con null. En salas del servidor solo puede hacerlo quien la creó.
   */
  | { t: 'host:schedule'; startAt: number | null }
  | { t: 'answer:submit'; questionIndex: number; payload: AnswerPayload }
  /** Jugador → servidor: lote de eventos de grabación (rrweb). Solo en salas supervisadas. */
  | { t: 'rec:events'; events: unknown[] }
  /** El jugador acepta ser grabado en esta sala supervisada. Sin esto no se graba nada. */
  | { t: 'rec:consent' }
  | { t: 'rec:flag'; kind: MonitorFlagKind }
  /** Anfitrión: empieza a ver en vivo a un jugador (null = deja de ver). */
  | { t: 'host:watch'; playerId: string | null }
  | { t: 'ping' };

export type ServerMessage =
  | {
      t: 'hello';
      userId: string;
      displayName: string;
      dahus: number;
      isGuest: boolean;
      /**
       * Por qué no se aceptó el apodo pedido, si no se aceptó. Antes el
       * servidor renombraba a "Invitado 1234" sin decir nada y quien lo sufría
       * no tenía forma de saber qué había escrito mal.
       */
      nicknameRejected?: string;
      /** Solo para invitados: lo que hay que reenviar al reconectar. */
      guestToken?: string;
    }
  | { t: 'error'; code: string; message: string }
  | { t: 'room:state'; room: PublicRoom }
  | { t: 'lobby:list'; rooms: LobbyRoom[] }
  | { t: 'room:countdown'; secondsLeft: number }
  | { t: 'room:question'; question: PublicQuestion; endsAt: number; serverNow: number }
  | { t: 'room:answered'; playerId: string; answeredCount: number }
  | { t: 'room:reveal'; reveal: RevealPayload }
  | { t: 'room:podium'; podium: PodiumEntry[]; prizePool: number; teams?: TeamStanding[] }
  /** Pregunta por pregunta: completo al anfitrión, el suyo a cada jugador. */
  | { t: 'room:report'; report: GameReport }
  /** Respuesta a `rec:consent`: `recorded` es false si a esta cuenta no se le graba (menor). */
  | { t: 'rec:consent'; recorded: boolean }
  /** Solo al anfitrión: eventos de grabación del jugador que está viendo. */
  | { t: 'rec:live'; playerId: string; events: unknown[]; reset?: boolean }
  /** Solo al anfitrión: avisos de posible trampa por jugador. */
  | { t: 'rec:alerts'; alerts: Record<string, MonitorAlert> }
  | { t: 'room:closed'; reason: string }
  /** Un amigo te invita a su sala. */
  | { t: 'invite'; invite: RoomInvite }
  /** Te llegó una solicitud de amistad o te aceptaron una: hay que refrescar. */
  | { t: 'friends:changed' }
  /** Resultado de `room:invite`: a cuántos les llegó. */
  | { t: 'room:invited'; sent: number }
  | { t: 'kicked' }
  | { t: 'pong'; serverNow: number };

/* ------------------------------------------------------------------ *
 * Puntuación — una sola definición, ejecutada solo en el servidor
 * ------------------------------------------------------------------ */

export const SCORING = {
  /** Puntos de una pregunta contestada bien e instantáneamente. */
  basePoints: 1000,
  /** Contestar al límite de tiempo conserva esta fracción. Recompensa velocidad sin castigar de más. */
  minSpeedFactor: 0.5,
  /** Bonus por acierto consecutivo, tope incluido. */
  streakBonusPerLevel: 60,
  maxStreakLevel: 6,
} as const;

/**
 * Cuánto conserva una respuesta según lo rápido que llegó, relativo al tiempo
 * límite de la pregunta: 1 al instante, `minSpeedFactor` al límite.
 */
export function speedFactor(responseTimeMs: number, timeLimitSec: number): number {
  const limitMs = Math.max(1, timeLimitSec * 1000);
  const elapsed = Math.min(Math.max(responseTimeMs, 0), limitMs);
  return 1 - (1 - SCORING.minSpeedFactor) * (elapsed / limitMs);
}

export function computePoints(args: {
  isCorrect: boolean;
  /** 0..1 — crédito parcial en selección múltiple. */
  correctness: number;
  responseTimeMs: number;
  timeLimitSec: number;
  pointsMultiplier: number;
  streakBefore: number;
}): number {
  if (!args.isCorrect || args.pointsMultiplier === 0) return 0;

  const base =
    SCORING.basePoints *
    args.pointsMultiplier *
    args.correctness *
    speedFactor(args.responseTimeMs, args.timeLimitSec);
  const streakLevel = Math.min(args.streakBefore, SCORING.maxStreakLevel);
  const streakBonus = streakLevel * SCORING.streakBonusPerLevel;

  return Math.round(base + streakBonus);
}
